import { CmsSectionShell } from './CmsSectionShell.tsx';
import { themeOf, RICHTEXT } from './cmsTheme.ts';
import { EnlargeableImage } from '../EnlargeableImage.tsx';
import { cdnSrcSet, cdnVariant } from '../../lib/cdn.ts';
import type { ContentSection, PageImage } from '../../lib/types.ts';

/**
 * CMS content section: a sanitized HTML body (headers are <h2>/<h3> within it).
 * The theme controls foreground/background colors and the guitar fill.
 *
 * - No image: the text uses the full container width.
 * - With image: a two-column text/image layout; `imageSide` puts the image on
 *   the left or right (defaults to right). Stacks on mobile (text, then image).
 * - `imageEnlargeable`: the image becomes a button that opens a full-screen view with a close
 *   icon in the top-right corner. Used for maps, where the whole (uncropped) image matters — so
 *   the in-page image is fitted rather than cropped too.
 */
export function CmsSection({ theme, guitar, bodyHtml, image, imageSide = 'right', imageAlign = 'top', imageEnlargeable = false }: ContentSection) {
  const t = themeOf(theme);
  const alignClass = imageAlign === 'center' ? 'md:items-center' : 'md:items-start';
  const body = (
    <div
      className={`${RICHTEXT} ${t.headingChild} ${t.body}`}
      dangerouslySetInnerHTML={{ __html: bodyHtml }}
    />
  );

  return (
    <CmsSectionShell theme={theme} guitar={guitar}>
      {image ? (
        <div className={`grid md:grid-cols-2 gap-10 md:gap-16 ${alignClass}`}>
          <div className={imageSide === 'left' ? 'md:order-2' : ''}>{body}</div>
          <div className={imageSide === 'left' ? 'md:order-1' : ''}>
            {imageEnlargeable
              ? <EnlargeableImage image={image} bgClass={t.img} sizes="(min-width: 768px) 50vw, 100vw" />
              : <FittedImage image={image} bgClass={t.img} />}
          </div>
        </div>
      ) : (
        body
      )}
    </CmsSectionShell>
  );
}

/** The plain, non-clickable image: a 3:2 card with the image cropped to fill it. */
function FittedImage({ image, bgClass }: { image: PageImage; bgClass: string }) {
  return (
    <div className={`aspect-3/2 w-full rounded-card overflow-hidden ${bgClass}`}>
      <img
        src={cdnSrcSet(image.src) ? cdnVariant(image.src, 'mobile') : image.src}
        srcSet={cdnSrcSet(image.src) ?? undefined}
        sizes="(min-width: 768px) 50vw, 100vw"
        alt={image.alt}
        className="w-full h-full object-cover"
        loading="lazy"
      />
    </div>
  );
}
